// SUMMARY: Consent state for § 25 TTDSG / GDPR. Two categories only, because that is what this app
// actually stores - there are NO analytics, advertising or third-party tracking technologies, and a banner
// that claimed otherwise would itself be misleading. Necessary storage needs no consent (§ 25 Abs. 2
// TTDSG); everything else is opt-in, off by default, and revocable at any time.

'use client'

// ===============================
// SECTION: MODEL
// ===============================

export type ConsentCategory = 'necessary' | 'functional'

export interface ConsentState {
	/** Always true - listed for transparency, never a choice (§ 25 Abs. 2 Nr. 2 TTDSG). */
	necessary: true
	/** Comfort settings stored on the device: chat response style, web-search toggle, sidebar tab. */
	functional: boolean
	/** Bumped when the categories or their purposes change, which re-asks everyone. */
	version: number
	/** ISO timestamp - the GDPR Art. 7(1) record of WHEN consent was given. */
	decidedAt: string
}

/** Raise this whenever a category is added or its purpose materially changes; it invalidates old consent. */
export const CONSENT_VERSION = 1

const COOKIE_NAME = 'evuno-consent'
/** Re-ask after six months, in line with German supervisory-authority guidance on consent lifetime. */
const COOKIE_MAX_AGE_SECONDS = 60 * 60 * 24 * 180

/** Fires on every decision so open components (and the storage guard) react without a reload. */
export const CONSENT_CHANGED = 'evuno:consent-changed'

/**
 * Device storage that is only allowed once `functional` consent is given. Necessary keys (auth session,
 * locale, theme, the consent record itself) are deliberately absent - they are exempt and must survive a
 * rejection.
 */
const FUNCTIONAL_STORAGE_KEYS = [
	'evuno:sidebar-recents-tab', // which Recents tab the sidebar opens on
	'euro-ai-chat-mode', // response-style picker
	'euro-ai-chat-search', // web-search toggle
	'euro-ai-chat-model-mode', // model picker mode (admin lab)
	'euro-ai-demo-remaining', // free landing-demo turns left
]

// ===============================
// SECTION: READ
// ===============================

function parse(raw: string): ConsentState | null {
	try {
		const parsed = JSON.parse(decodeURIComponent(raw)) as Partial<ConsentState>
		if (parsed.version !== CONSENT_VERSION) return null
		return {
			necessary: true,
			functional: parsed.functional === true,
			version: CONSENT_VERSION,
			decidedAt: typeof parsed.decidedAt === 'string' ? parsed.decidedAt : new Date().toISOString(),
		}
	} catch {
		return null
	}
}

/** Current decision, or null when the user has not decided yet (or the version moved on). */
export function readConsent(): ConsentState | null {
	if (typeof document === 'undefined') return null
	const entry = document.cookie.split('; ').find((part) => part.startsWith(`${COOKIE_NAME}=`))
	return entry ? parse(entry.slice(COOKIE_NAME.length + 1)) : null
}

/** Whether a given category may be used right now. Necessary is always allowed. */
export function hasConsent(category: ConsentCategory): boolean {
	if (category === 'necessary') return true
	return readConsent()?.functional === true
}

// ===============================
// SECTION: WRITE
// ===============================

function persist(state: ConsentState): void {
	const value = encodeURIComponent(JSON.stringify(state))
	const secure = window.location.protocol === 'https:' ? '; Secure' : ''
	// SameSite=Lax + no third party: the consent record is itself strictly necessary storage.
	document.cookie = `${COOKIE_NAME}=${value}; path=/; max-age=${COOKIE_MAX_AGE_SECONDS}; SameSite=Lax${secure}`
	window.dispatchEvent(new Event(CONSENT_CHANGED))
}

/**
 * Record a decision. Withdrawing `functional` consent also DELETES what was stored under it - consent
 * withdrawal that leaves the data in place would be withdrawal in name only (Art. 7(3) GDPR).
 */
export function saveConsent(functional: boolean): ConsentState {
	const state: ConsentState = {
		necessary: true,
		functional,
		version: CONSENT_VERSION,
		decidedAt: new Date().toISOString(),
	}
	persist(state)
	if (!functional) purgeFunctionalStorage()
	return state
}

export function purgeFunctionalStorage(): void {
	try {
		for (const key of FUNCTIONAL_STORAGE_KEYS) localStorage.removeItem(key)
	} catch {
		/* private mode / storage disabled - nothing to purge */
	}
}

/**
 * Write to device storage only if the category was consented to. Callers use this instead of touching
 * localStorage directly, so a rejection is actually honoured rather than merely recorded.
 */
export function setFunctionalStorage(key: string, value: string): void {
	if (!hasConsent('functional')) return
	try {
		localStorage.setItem(key, value)
	} catch {
		/* private mode / storage disabled */
	}
}
